/**
 * App-shell-level provider for the active video consultation.
 *
 * Lifted out of `ConsultationVideoLayout`/`/consultations/:id` so the call can
 * survive navigating to `/patients/:id` (or anywhere else) — a route's element
 * unmounts the moment the doctor navigates away, which would otherwise tear
 * down the LiveKit connection. `FloatingConsultationPanel`, mounted once in
 * `ProtectedShell` (outside `<Outlet />`), reads this context and renders the
 * live call regardless of which route is active underneath it.
 *
 * ── Docking ─────────────────────────────────────────────────────────────────
 * The consultation room (v2) wants the same live call to appear at the top of
 * its prescription rail rather than as a slab against the right edge of the
 * viewport. It does NOT get its own <LiveKitRoom> for that: a second Room would
 * mean a second connection, and switching between the two would drop the call.
 *
 * Instead the room registers a plain DOM node here — a *dock target* — and the
 * floating panel relocates the (single, never-remounted) DOM subtree its call
 * lives in into that node. Registration is a DOM node rather than a boolean
 * because the panel needs somewhere to put the call, and a ref callback is the
 * only thing that reliably tells us when that somewhere appears and disappears.
 *
 * `dockTarget === null` is the normal, un-docked state and the fallback for
 * every path where the room isn't on screen: doctor navigated away, the v1
 * layout is in use, the room crashed. The call then renders floating, exactly as
 * it did before docking existed.
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

export interface DoctorJoinResponse {
  room_id: string;
  token: string;
  endpoint: string;
}

interface CallSessionState {
  consultationId: string;
  patientName: string;
  /** Reference point for the in-call elapsed timer (see ElapsedTimer). */
  scheduledStartAt: string;
  joinResp: DoctorJoinResponse;
  minimized: boolean;
}

/** How the call is currently presented. Derived, never stored.
 *
 *  Docking and minimizing used to be mutually exclusive — `docked` won, because
 *  a docked call had nowhere to minimize *to*. It does now: the room's rail
 *  slot collapses to a slim live bar, handing the rail's full height to the
 *  prescription form for a doctor who needs to read a long lab history without
 *  leaving the call. So the two combine into `docked-collapsed` rather than one
 *  overriding the other.
 *
 *  All four are the same element tree — see `CallShell`. Nothing here unmounts
 *  the call; presentation only changes chrome and what is hidden. */
export type CallPresentation = 'floating' | 'minimized' | 'docked' | 'docked-collapsed';

interface CallSessionContextValue {
  session: CallSessionState | null;
  /** The element the call's DOM subtree is currently relocated into, or null. */
  dockTarget: HTMLElement | null;
  presentation: CallPresentation;
  startCall: (
    consultationId: string,
    patientName: string,
    scheduledStartAt: string,
    joinResp: DoctorJoinResponse,
  ) => void;
  endCall: () => void;
  minimize: () => void;
  maximize: () => void;
  /**
   * Register (or, with null, unregister) the node the call should be docked
   * into. Stable identity — safe to pass straight to a `ref` attribute, which
   * is how DockedVideo uses it: React calls it with the node on mount and with
   * null on unmount, so undocking on navigation needs no cleanup of its own.
   *
   * Only one dock target is meaningful at a time (there is only one call). A
   * second registration replaces the first; the room mounts exactly one
   * DockedVideo, so that case only arises transiently during a route swap.
   */
  registerDockTarget: (el: HTMLElement | null) => void;
  /** Registered by whichever call-entry UI last started a call; invoked once
   *  when that call ends (disconnect or explicit leave) so it can react —
   *  e.g. refresh the consultation query so a stale "in_progress" status
   *  updates once the doctor is back on that page. */
  registerOnEnd: (fn: (consultationId: string) => void) => void;
}

const CallSessionContext = createContext<CallSessionContextValue | null>(null);

export function CallSessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<CallSessionState | null>(null);
  const [dockTarget, setDockTarget] = useState<HTMLElement | null>(null);
  const onEndRef = useRef<((consultationId: string) => void) | null>(null);

  const startCall = useCallback(
    (consultationId: string, patientName: string, scheduledStartAt: string, joinResp: DoctorJoinResponse) => {
      setSession({ consultationId, patientName, scheduledStartAt, joinResp, minimized: false });
    },
    [],
  );

  const endCall = useCallback(() => {
    setSession(prev => {
      if (prev) onEndRef.current?.(prev.consultationId);
      return null;
    });
  }, []);

  const minimize = useCallback(() => {
    setSession(prev => (prev ? { ...prev, minimized: true } : prev));
  }, []);

  const maximize = useCallback(() => {
    setSession(prev => (prev ? { ...prev, minimized: false } : prev));
  }, []);

  const registerDockTarget = useCallback((el: HTMLElement | null) => {
    // Identity-compare so React's ref churn (it may call a ref with the same
    // node again after an unrelated re-render) doesn't publish a new state
    // value and re-run the panel's relocation effect for no reason.
    setDockTarget(prev => (prev === el ? prev : el));
  }, []);

  const registerOnEnd = useCallback((fn: (consultationId: string) => void) => {
    onEndRef.current = fn;
  }, []);

  const presentation: CallPresentation =
    dockTarget !== null
      ? session?.minimized
        ? 'docked-collapsed'
        : 'docked'
      : session?.minimized
        ? 'minimized'
        : 'floating';

  const value = useMemo(
    () => ({
      session,
      dockTarget,
      presentation,
      startCall,
      endCall,
      minimize,
      maximize,
      registerDockTarget,
      registerOnEnd,
    }),
    [
      session, dockTarget, presentation,
      startCall, endCall, minimize, maximize, registerDockTarget, registerOnEnd,
    ],
  );

  return <CallSessionContext.Provider value={value}>{children}</CallSessionContext.Provider>;
}

export function useCallSession(): CallSessionContextValue {
  const ctx = useContext(CallSessionContext);
  if (!ctx) throw new Error('useCallSession must be used within CallSessionProvider');
  return ctx;
}
